import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Space, Row, Col, Layout } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { SERVER_IP } from 'assets/Config';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import TemplateMetadataForm from './components/TemplateMetadataForm';
import DayNavigator from './components/DayNavigator';
import DayEditor from './components/DayEditor';
import ExercisePickerDrawer from './components/ExercisePickerDrawer';
import { MOCK_WORKOUT_TEMPLATES } from 'data/workoutMockData';
import { useI18n } from 'hooks/useI18n';

import './template-builder.scss';

const { Content, Sider } = Layout;

/* ── Local state management ──────────────────────────────────── */
const buildInitialDays = (count) =>
	Array.from({ length: count }, (_, i) => ({
		dayNumber: i + 1,
		isRestDay: false,
		title: '',
		items: [],
	}));

const getWorkoutTemplateEntity = (payload) => {
	if (!payload) {
		return null;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data[0] || null;
	}

	if (payload?.data && !Array.isArray(payload.data)) {
		return payload.data;
	}

	if (Array.isArray(payload)) {
		return payload[0] || null;
	}

	return payload;
};

const getWorkoutId = (workout) =>
	workout?.id ||
	workout?._id ||
	workout?.workoutId ||
	workout?.data?.id ||
	workout?.data?._id ||
	workout?.exercise?.id ||
	workout?.exercise?._id ||
	workout?.workout?.id ||
	workout?.workout?._id ||
	null;

const getWorkoutName = (workout) =>
	workout?.workoutName ||
	workout?.name ||
	workout?.title ||
	workout?.exerciseName ||
	workout?.label ||
	workout?.data?.workoutName ||
	workout?.data?.name ||
	workout?.data?.title ||
	workout?.data?.exerciseName ||
	workout?.exercise?.name ||
	workout?.exercise?.title ||
	workout?.workout?.name ||
	workout?.workout?.title ||
	'Unnamed exercise';

const getWorkoutDefaultSets = (workout) =>
	workout?.defaultSets ?? workout?.sets ?? workout?.data?.defaultSets ?? workout?.workout?.defaultSets ?? 3;

const getWorkoutDefaultReps = (workout) =>
	workout?.defaultReps ?? workout?.reps ?? workout?.data?.defaultReps ?? workout?.workout?.defaultReps ?? '10';

const getWorkoutDefaultRestSeconds = (workout) =>
	workout?.defaultRestSeconds ?? workout?.restSeconds ?? workout?.data?.defaultRestSeconds ?? workout?.workout?.defaultRestSeconds ?? 60;

const normalizeTemplateItem = (item, index) => ({
	...item,
	id: item?.id || item?._id || `item-${index}`,
	workoutId: getWorkoutId(item),
	workoutName: getWorkoutName(item),
	sets: item?.sets ?? getWorkoutDefaultSets(item),
	reps: item?.reps ?? getWorkoutDefaultReps(item),
	restSeconds: item?.restSeconds ?? getWorkoutDefaultRestSeconds(item),
	notes: item?.notes || '',
	order: item?.order || item?.sequence || index + 1,
});

const normalizeTemplateDay = (day, index) => ({
	...day,
	id: day?.id || day?._id || `day-${index + 1}`,
	dayNumber: day?.dayNumber || index + 1,
	isRestDay: Boolean(day?.isRestDay),
	title: day?.title || '',
	items: Array.isArray(day?.items) ? day.items.map((item, itemIndex) => normalizeTemplateItem(item, itemIndex)) : [],
});

const normalizeWorkoutTemplate = (payload) => {
	const template = getWorkoutTemplateEntity(payload);

	if (!template) {
		return null;
	}

	const normalizedDays = Array.isArray(template.days)
		? template.days.map((day, index) => normalizeTemplateDay(day, index))
		: buildInitialDays(template.durationDays || 28);

	return {
		...template,
		id: template.id || template._id,
		name: template.name || '',
		durationDays: template.durationDays || normalizedDays.length || 28,
		gender: template.gender || undefined,
		level: template.level || undefined,
		goal: template.goal || undefined,
		days: normalizedDays,
	};
};

function builderReducer(state, action) {
	switch (action.type) {
		case 'LOAD': {
			return {
				...state,
				days: action.payload.days || buildInitialDays(action.payload.durationDays || 28),
				selectedDayNumber: 1,
			};
		}
		case 'SYNC_DAYS': {
			const count = action.count;
			const existing = state.days;
			if (count === existing.length) return state;
			if (count > existing.length) {
				const extra = Array.from({ length: count - existing.length }, (_, i) => ({
					dayNumber: existing.length + i + 1,
					isRestDay: false,
					title: '',
					items: [],
				}));
				return { ...state, days: [...existing, ...extra] };
			}
			return { ...state, days: existing.slice(0, count) };
		}
		case 'SELECT_DAY':
			return { ...state, selectedDayNumber: action.dayNumber };
		case 'TOGGLE_REST': {
			const days = state.days.map((d) =>
				d.dayNumber === action.dayNumber ? { ...d, isRestDay: !d.isRestDay, items: d.isRestDay ? d.items : [] } : d
			);
			return { ...state, days };
		}
		case 'CHANGE_TITLE': {
			const days = state.days.map((d) =>
				d.dayNumber === action.dayNumber ? { ...d, title: action.title } : d
			);
			return { ...state, days };
		}
		case 'ADD_EXERCISE': {
			const { dayNumber, workout } = action;
			const days = state.days.map((d) => {
				if (d.dayNumber !== dayNumber) return d;
				const newItem = {
					id: `new-${Date.now()}-${Math.random()}`,
					workoutId: getWorkoutId(workout),
					workoutName: getWorkoutName(workout),
					sets: getWorkoutDefaultSets(workout),
					reps: getWorkoutDefaultReps(workout),
					restSeconds: getWorkoutDefaultRestSeconds(workout),
					notes: '',
					order: (d.items?.length || 0) + 1,
				};
				return { ...d, items: [...(d.items || []), newItem] };
			});
			return { ...state, days };
		}
		case 'CHANGE_ITEM': {
			const { dayNumber, index, item } = action;
			const days = state.days.map((d) => {
				if (d.dayNumber !== dayNumber) return d;
				const items = [...(d.items || [])];
				items[index] = item;
				return { ...d, items };
			});
			return { ...state, days };
		}
		case 'REMOVE_ITEM': {
			const { dayNumber, index } = action;
			const days = state.days.map((d) => {
				if (d.dayNumber !== dayNumber) return d;
				const items = (d.items || []).filter((_, i) => i !== index);
				return { ...d, items };
			});
			return { ...state, days };
		}
		default:
			return state;
	}
}

/* ── Component ─────────────────────────────────────────────────── */
const TemplateBuilder = () => {
	const { id } = useParams();
	const isEdit = Boolean(id);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutTemplateRedux = useSelector((state) => state.workoutTemplateRedux);

	const [form] = Form.useForm();
	const [pickerOpen, setPickerOpen] = useState(false);
	const [builderState, builderDispatch] = useReducer(builderReducer, {
		days: buildInitialDays(28),
		selectedDayNumber: 1,
	});

	/* Load existing template when editing */
	useEffect(() => {
		if (isEdit) {
			dispatch(getApi('GET_WORKOUT_TEMPLATE_BY_ID', `${SERVER_IP}api/workoutTemplate/${id}`));
		}
	}, [isEdit, id, dispatch]);

	useEffect(() => {
		if (isEdit) {
			const tpl =
				normalizeWorkoutTemplate(workoutTemplateRedux?.currentTemplate) ||
				normalizeWorkoutTemplate(MOCK_WORKOUT_TEMPLATES.find((t) => t.id === id || t._id === id));
			if (tpl) {
				form.setFieldsValue({
					name: tpl.name,
					durationDays: tpl.durationDays,
					gender: tpl.gender,
					level: tpl.level,
					goal: tpl.goal,
				});
				builderDispatch({ type: 'LOAD', payload: tpl });
			}
		}
	}, [isEdit, workoutTemplateRedux?.currentTemplate, form, id]);

	/* Sync day count when duration changes */
	const watchedDuration = Form.useWatch('durationDays', form);
	useEffect(() => {
		if (watchedDuration && watchedDuration > 0) {
			builderDispatch({ type: 'SYNC_DAYS', count: watchedDuration });
		}
	}, [watchedDuration]);

	/* Redirect on save success */
	useEffect(() => {
		const apiName = isEdit ? 'EDIT_WORKOUT_TEMPLATE' : 'ADD_WORKOUT_TEMPLATE';
		if (globalRedux.apiStatus[apiName] === 'SUCCESS') {
			dispatch(resetApiStatus(apiName));
			navigate('/workout-templates');
		}
	}, [globalRedux.apiStatus, isEdit, dispatch, navigate]);

	/* Derived */
	const selectedDay = builderState.days.find((d) => d.dayNumber === builderState.selectedDayNumber);

	const handleSave = useCallback(async () => {
		try {
			const meta = await form.validateFields();
			const payload = { ...meta, days: builderState.days };
			if (isEdit) {
				dispatch(putApi(payload, 'EDIT_WORKOUT_TEMPLATE', `${SERVER_IP}api/workoutTemplate/${id}`));
			} else {
				dispatch(postApi(payload, 'ADD_WORKOUT_TEMPLATE', `${SERVER_IP}api/workoutTemplate`));
			}
		} catch {
			// Form validation failed — Ant Design shows inline errors
		}
	}, [form, builderState.days, isEdit, dispatch, id]);

	const handleSelectExercise = useCallback(
		(workout) => {
			builderDispatch({
				type: 'ADD_EXERCISE',
				dayNumber: builderState.selectedDayNumber,
				workout,
			});
			setPickerOpen(false);
		},
		[builderState.selectedDayNumber]
	);

	const saving =
		globalRedux.apiStatus.ADD_WORKOUT_TEMPLATE === 'PENDING' ||
		globalRedux.apiStatus.EDIT_WORKOUT_TEMPLATE === 'PENDING';

	return (
		<div className="template-builder-page">
			<div className="template-builder-page__shell">
				<div className="template-builder-page__hero">
					<div className="template-builder-page__hero-copy">
						<span className="template-builder-page__eyebrow">{t('workouts.template.builder.eyebrow')}</span>
						<h1 className="template-builder-page__title">{isEdit ? t('workouts.template.builder.edit_title') : t('workouts.template.builder.create_title')}</h1>
						<p className="template-builder-page__subtitle">
							{t('workouts.template.builder.subtitle')}
						</p>
					</div>
					<Form className="template-builder-page__hero-form" form={form} layout="inline" initialValues={{ durationDays: 28 }}>
						<Row gutter={[12, 12]} className="template-builder-page__hero-row" align="middle">
							<Col flex="auto" className="template-builder-page__metadata-col">
								<TemplateMetadataForm form={form} />
							</Col>
							<Col className="template-builder-page__actions-col" flex="none">
								<Space className="template-builder-page__hero-actions" size={10}>
									<Button className="template-builder-page__secondary-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate('/workout-templates')}>
										{t('common.cancel')}
									</Button>
									<Button
										className="template-builder-page__primary-btn"
										type="primary"
										icon={<SaveOutlined />}
										onClick={handleSave}
										loading={saving}
									>
										{isEdit ? t('workouts.template.builder.save_changes') : t('workouts.template.builder.create_template')}
									</Button>
								</Space>
							</Col>
						</Row>
					</Form>
				</div>

				<Layout className="template-builder-page__layout">
				<Sider
					width={220}
						className="template-builder-page__sider"
				>
						<div className="template-builder-page__sider-header">
						{t('workouts.common.days_count', { count: builderState.days.length })}
					</div>
					<DayNavigator
						days={builderState.days}
						selectedDayNumber={builderState.selectedDayNumber}
						onSelectDay={(n) => builderDispatch({ type: 'SELECT_DAY', dayNumber: n })}
					/>
				</Sider>

					<Content className="template-builder-page__content">
					<DayEditor
						day={selectedDay}
						onToggleRest={() =>
							builderDispatch({ type: 'TOGGLE_REST', dayNumber: builderState.selectedDayNumber })
						}
						onTitleChange={(title) =>
							builderDispatch({ type: 'CHANGE_TITLE', dayNumber: builderState.selectedDayNumber, title })
						}
						onItemChange={(index, item) =>
							builderDispatch({ type: 'CHANGE_ITEM', dayNumber: builderState.selectedDayNumber, index, item })
						}
						onItemRemove={(index) =>
							builderDispatch({ type: 'REMOVE_ITEM', dayNumber: builderState.selectedDayNumber, index })
						}
						onAddExercise={() => setPickerOpen(true)}
					/>
				</Content>
			</Layout>
			</div>

			<ExercisePickerDrawer
				open={pickerOpen}
				onClose={() => setPickerOpen(false)}
				onSelect={handleSelectExercise}
			/>
		</div>
	);
};

export default TemplateBuilder;
